import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  toAgentErrorMessage,
  toRejectedUrlMessage,
  VALIDATION_FAILED,
} from 'features/yieldbirdWrapperAgent/api/errors'
import { reportsQueryKey } from 'features/yieldbirdWrapperAgent/api/queries'
import { createReport } from 'features/yieldbirdWrapperAgent/api/requests'
import { enqueueSnackbarForRestError } from 'providers/tanstack-query-rest/errors'

import type { Schema } from './schema'
import type { FieldError, FieldErrors } from 'react-hook-form'

type FailedUrl = {
  error: unknown
  rejectedUrlMessage?: string
  url: string
}

type UseCreateReports = {
  create: (data: Schema) => Promise<void>
  errors: FieldErrors<Schema>
  isPending: boolean
  remainingUrls?: Schema['urls']
}

const queueReports = async (urls: string[]): Promise<FailedUrl[]> =>
  (
    await Promise.all(
      urls.map(async (url: string): Promise<FailedUrl[]> => {
        try {
          await createReport({ url })

          return []
        } catch (error) {
          return [{ error, rejectedUrlMessage: toRejectedUrlMessage(error), url }]
        }
      }),
    )
  ).flat()

const toFieldErrors = (failedUrls: FailedUrl[]): FieldErrors<Schema> =>
  failedUrls.some(({ rejectedUrlMessage }: FailedUrl): boolean => Boolean(rejectedUrlMessage))
    ? {
        urls: failedUrls.map(({ rejectedUrlMessage }: FailedUrl): FieldError | undefined =>
          rejectedUrlMessage === undefined
            ? undefined
            : { message: rejectedUrlMessage, type: VALIDATION_FAILED },
        ),
      }
    : {}

export const useCreateReports = (): UseCreateReports => {
  const queryClient = useQueryClient()
  const [isPending, setIsPending] = useState<boolean>(false)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})
  const [remainingUrls, setRemainingUrls] = useState<Schema['urls']>()

  const create = async ({ urls }: Schema): Promise<void> => {
    setIsPending(true)
    const failedUrls: FailedUrl[] = await queueReports(urls)
    setIsPending(false)

    const otherFailure: FailedUrl | undefined = failedUrls.find(
      ({ rejectedUrlMessage }: FailedUrl): boolean => rejectedUrlMessage === undefined,
    )
    if (otherFailure) {
      enqueueSnackbarForRestError(otherFailure.error, toAgentErrorMessage)
    }

    setErrors(toFieldErrors(failedUrls))
    setRemainingUrls(failedUrls.map(({ url }: FailedUrl): string => url))

    void queryClient.invalidateQueries({ queryKey: reportsQueryKey })
  }

  return {
    create,
    errors,
    isPending,
    remainingUrls,
  }
}
